import {
  useCallback,
  useMemo,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { View } from "vega";
import type { TopLevelSpec } from "vega-lite";
import { fitToHost, type HostSize } from "../custom/fitToHost.js";
import type { ChartDetail } from "../fit/detail.js";
import { mergeConfig } from "../theme/config.js";
import { useVizTheme, type UseVizThemeOptions } from "../theme/useVizTheme.js";
import type { ChartData } from "../types.js";
import { EmbedFrame, useSafeEmbedOptions } from "./EmbedFrame.js";
import { registerVizExpressions } from "./expressions.js";

registerVizExpressions();

export interface VegaChartProps {
  /** The Vega-Lite spec to render. The themed config is merged in automatically. */
  spec: TopLevelSpec;
  /** Optional inline data; overrides `spec.data` when provided. */
  data?: ChartData;
  /**
   * Total chart height in pixels — axes, legend and title included, so the
   * chart never overflows a container of this height.
   */
  height?: number;
  /** Chart width — defaults to `"container"` (fills and tracks the parent width). */
  width?: number | "container";
  /**
   * The container's width, when the host has measured it: sizes the first
   * paint (before the chart measures its container) and, unless `detail` is
   * given, decides how much detail fits.
   */
  containerWidth?: number;
  /** How much to draw (`chartDetailForSize`); derived from the size when unset. */
  detail?: ChartDetail;
  /** Vega renderer. SVG (default) is crisp and theme-able; canvas is faster for large data. */
  renderer?: "svg" | "canvas";
  /** Show Vega's export/source actions menu. Off by default. */
  actions?: boolean;
  /** Show hover tooltips. On by default. */
  tooltip?: boolean;
  /** Theme overrides (custom palette, font, or token root element). */
  theme?: UseVizThemeOptions;
  className?: string;
  style?: CSSProperties;
  /** Called with the Vega {@link View} once the chart has embedded. */
  onNewView?: (view: View) => void;
  /** Called if embedding fails. */
  onError?: (error: unknown) => void;
  /** Rendered in place of the chart when embedding fails. */
  errorFallback?: ReactNode;
}

const DEFAULT_HEIGHT = 300;
const FALLBACK_WIDTH = 300;

/**
 * The host size a chart is fitted to: a fixed width, or the container's
 * (its measured width until the chart measures it, else a typical one).
 */
export function hostSizeOf({
  height,
  width,
  containerWidth,
  detail,
}: {
  height: number;
  width: number | "container";
  containerWidth?: number;
  detail?: ChartDetail;
}): HostSize {
  const size: HostSize =
    typeof width === "number"
      ? { height, fallbackWidth: width, fixedWidth: width }
      : {
          height,
          fallbackWidth:
            containerWidth && containerWidth > 0
              ? containerWidth
              : FALLBACK_WIDTH,
        };
  return detail ? { ...size, detail } : size;
}

/**
 * Low-level chart renderer. Resolves the active theme, merges it into the
 * spec's config, and embeds the chart responsively. Most callers should reach
 * for the high-level components (`BarChart`, `LineChart`, …), but this is the
 * escape hatch for hand-written Vega-Lite specs.
 */
// Its complexity is its prop list with defaults; sizing lives in hostSizeOf.
// fallow-ignore-next-line complexity
export function VegaChart({
  spec,
  data,
  height = DEFAULT_HEIGHT,
  width = "container",
  containerWidth,
  detail,
  renderer = "svg",
  actions = false,
  tooltip = true,
  theme,
  className,
  style,
  onNewView,
  onError,
  errorFallback,
}: VegaChartProps): ReactNode {
  const { config, isDark } = useVizTheme(theme);

  const mergedSpec = useMemo<TopLevelSpec>(() => {
    const withData = data ? { ...spec, data: { values: [...data] } } : spec;
    return {
      width,
      height,
      ...withData,
      config: mergeConfig(config, withData.config),
    };
  }, [spec, data, config, width, height]);

  // Size the compiled spec to the host: `fit` autosizing makes `height` the
  // chart's total height rather than the plot's, and caps long titles.
  const patch = useMemo(
    () => (compiled: Parameters<typeof fitToHost>[0]) =>
      fitToHost(
        compiled,
        hostSizeOf({ height, width, containerWidth, detail }),
      ),
    [height, width, containerWidth, detail],
  );

  const options = useSafeEmbedOptions({
    mode: "vega-lite",
    renderer,
    actions,
    tooltip,
    isDark,
    patch,
  });

  const fallback = useCallback(() => errorFallback, [errorFallback]);

  return (
    <EmbedFrame
      className={className}
      style={style}
      spec={mergedSpec}
      options={options}
      onNewView={onNewView}
      onError={onError}
      errorFallback={errorFallback === undefined ? undefined : fallback}
      trackWidth={width === "container"}
    />
  );
}
